import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { INTAKE } from "@/constants/testIds";
import NarratedText from "@/components/NarratedText";

const REFLECTIONS = [
  "What are you actively releasing in this season?",
  "Name a symbol, animal, or object that has been finding you.",
  "When you were younger, what did you know that adults did not?",
  "What kind of silence do you need more of?",
  "If your soul had a season, which one is it in right now?",
];

// Small muted "reason" text under each input
function Reason({ children }) {
  return (
    <div
      className="mt-2"
      style={{
        color: "rgba(244,239,230,0.55)",
        fontFamily: '"DM Sans", system-ui, sans-serif',
        fontSize: 12,
        lineHeight: 1.5,
      }}
    >
      {children}
    </div>
  );
}

// Progress destinations shown beneath the progress indicator
// Steps: 0 intro · 1 soul name · 2 chart · 3-7 reflections
const NEXT_DESTINATIONS = {
  0: "Next: your soul name",
  1: "Next: your chart",
  2: "Next: your House Deck",
  3: "Next: your House Deck",
  4: "Next: your House Deck",
  5: "Next: your House Deck",
  6: "Next: your House Deck",
  7: "Next: your Soul Mark",
};

export default function SoulIntake() {
  const [step, setStep] = useState(0);
  const [birthName, setBirthName] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [birthTime, setBirthTime] = useState("12:00");
  const [unknownTime, setUnknownTime] = useState(false);
  const [birthLocation, setBirthLocation] = useState("");
  const [soulName, setSoulName] = useState("");
  const [reflections, setReflections] = useState(["", "", "", "", ""]);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const totalSteps = 3 + REFLECTIONS.length; // 0 intro · 1 soul name · 2 chart · 3-7 reflections

  const canAdvance = () => {
    if (step === 0) return true;
    if (step === 1) return true; // soul name optional
    if (step === 2) return birthName.trim().length >= 2 && birthDate.length === 10;
    const rIdx = step - 3;
    if (rIdx >= 0 && rIdx < REFLECTIONS.length) return reflections[rIdx].trim().length >= 3;
    return true;
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/soul/intake", {
        birth_name: birthName.trim(),
        birth_date: birthDate,
        birth_time: unknownTime ? "12:00" : birthTime || "12:00",
        birth_location: birthLocation.trim(),
        soul_name: soulName.trim() || null,
        reflections: reflections.map((r) => r.trim()),
      });
      toast.success("Your soul record is written.");
      navigate("/ceremony/welcome");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save intake.");
    } finally {
      setBusy(false);
    }
  };

  const setReflection = (i, v) => {
    const next = [...reflections];
    next[i] = v;
    setReflections(next);
  };

  return (
    <div className="relative min-h-screen bg-[#080808] overflow-hidden">
      <div className="starfield-subtle" />
      <div className="relative max-w-3xl mx-auto px-6 sm:px-10 py-16 sm:py-24">
        {/* Progress row */}
        <div className="flex items-center justify-between mb-2">
          <div className="font-overline">Soul Intake</div>
          <div className="font-overline text-[#F4EFE6]/50">
            {String(step + 1).padStart(2, "0")} / {String(totalSteps).padStart(2, "0")}
          </div>
        </div>
        {/* Next destination line */}
        <div
          data-testid={INTAKE.nextDestination}
          className="mb-10"
          style={{
            color: "rgba(244,239,230,0.45)",
            fontFamily: '"DM Sans", system-ui, sans-serif',
            fontSize: 12,
            letterSpacing: "0.02em",
          }}
        >
          {NEXT_DESTINATIONS[step] || ""}
        </div>

        <div className="relative min-h-[400px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              data-testid={INTAKE.step}
            >
              {step === 0 && (
                <div>
                  <h1 className="font-heading text-5xl sm:text-6xl text-[#F4EFE6] leading-tight font-light">
                    Before we draw
                    <br />
                    your deck…
                  </h1>
                  <NarratedText
                    id="intake-intro"
                    text="Five layers form your soul signature — your natal sky, your numerology, your elemental current, five written reflections, and the name your soul answers to. Answer slowly. Nothing here is graded."
                    as="p"
                    className="mt-8 text-lg text-[#F4EFE6]/70 max-w-xl leading-relaxed"
                    manual
                  />
                </div>
              )}

              {step === 1 && (
                <div>
                  <div className="font-overline mb-4">Layer one · Soul Name</div>
                  <h2 className="font-heading text-4xl sm:text-5xl text-[#F4EFE6] font-light mb-8">
                    What does your soul
                    <br />
                    answer to?
                  </h2>
                  <p className="text-[#F4EFE6]/60 max-w-xl mb-8">
                    A single word, a chosen name, or leave it blank to let your
                    given name carry through.
                  </p>
                  <div className="max-w-md">
                    <input
                      data-testid={INTAKE.soulNameInput}
                      className="input-sacred text-xl"
                      value={soulName}
                      onChange={(e) => setSoulName(e.target.value)}
                      placeholder="an inner name"
                      style={{ minHeight: 44 }}
                    />
                    <Reason>
                      This is the name printed on the back of your cards, and
                      what we&apos;ll call you from here.
                    </Reason>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div>
                  <div className="font-overline mb-4">Layer two · Chart</div>
                  <h2 className="font-heading text-4xl sm:text-5xl text-[#F4EFE6] font-light mb-12">
                    The sky when you arrived.
                  </h2>
                  <div className="space-y-10 max-w-xl">
                    <div>
                      <label className="font-overline block mb-2">Birth name</label>
                      <input
                        data-testid={INTAKE.birthNameInput}
                        className="input-sacred"
                        value={birthName}
                        onChange={(e) => setBirthName(e.target.value)}
                        placeholder="the full name given at birth"
                        style={{ minHeight: 44 }}
                      />
                      <Reason>
                        The letters carry numbers. Your Life Path, Expression
                        and Soul Urge all come from here.
                      </Reason>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                      <div>
                        <label className="font-overline block mb-2">Birth date</label>
                        <input
                          data-testid={INTAKE.birthDateInput}
                          className="input-sacred"
                          type="date"
                          value={birthDate}
                          onChange={(e) => setBirthDate(e.target.value)}
                          style={{ minHeight: 44 }}
                        />
                        <Reason>
                          Your birth sky. This shapes the symbols that appear
                          in your mark.
                        </Reason>
                      </div>
                      <div>
                        <label className="font-overline block mb-2">Birth time (approx)</label>
                        <input
                          data-testid={INTAKE.birthTimeInput}
                          className="input-sacred"
                          type="time"
                          value={birthTime}
                          onChange={(e) => setBirthTime(e.target.value)}
                          disabled={unknownTime}
                          style={{
                            minHeight: 44,
                            opacity: unknownTime ? 0.4 : 1,
                          }}
                        />
                        <Reason>
                          The sky moves fast. Time gives us your Moon and
                          Rising, not just your Sun.
                        </Reason>
                        {/* Unknown time toggle — visible immediately */}
                        <label
                          className="mt-3 flex items-start gap-2 cursor-pointer select-none"
                          style={{
                            fontFamily: '"DM Sans", system-ui, sans-serif',
                            fontSize: 12,
                            color: unknownTime
                              ? "#C8A96E"
                              : "rgba(244,239,230,0.65)",
                            minHeight: 32,
                          }}
                        >
                          <input
                            type="checkbox"
                            data-testid={INTAKE.birthTimeUnknown}
                            checked={unknownTime}
                            onChange={(e) => setUnknownTime(e.target.checked)}
                            className="mt-[3px] accent-[#C8A96E]"
                            style={{ width: 14, height: 14 }}
                          />
                          <span>I don&apos;t know my exact time</span>
                        </label>
                        {unknownTime && (
                          <div
                            className="mt-2"
                            style={{
                              color: "rgba(244,239,230,0.55)",
                              fontFamily: '"DM Sans", system-ui, sans-serif',
                              fontSize: 12,
                              lineHeight: 1.5,
                            }}
                          >
                            That&apos;s common, and it&apos;s fine. We&apos;ll use solar noon.
                            Everything except your house placements stays
                            exact.
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="font-overline block mb-2">Birth location</label>
                      <input
                        data-testid={INTAKE.birthLocationInput}
                        className="input-sacred"
                        value={birthLocation}
                        onChange={(e) => setBirthLocation(e.target.value)}
                        placeholder="city, region"
                        style={{ minHeight: 44 }}
                      />
                      <Reason>
                        Where the sky was overhead. City is enough.
                      </Reason>
                    </div>
                  </div>

                  {/* Reassurance line at the bottom of the first intake screen */}
                  <div
                    data-testid={INTAKE.reassurance}
                    className="mt-14 pt-6 border-t border-[#C8A96E]/15 max-w-xl"
                    style={{
                      color: "rgba(244,239,230,0.5)",
                      fontFamily: '"DM Sans", system-ui, sans-serif',
                      fontSize: 12,
                      lineHeight: 1.6,
                    }}
                  >
                    Your birth details are used to draw your mark and nothing
                    else. Never sold, never shared.
                  </div>
                </div>
              )}

              {step >= 3 && step < 3 + REFLECTIONS.length && (
                <div>
                  <div className="font-overline mb-4">
                    Reflection {step - 2} of {REFLECTIONS.length}
                  </div>
                  <NarratedText
                    id={`intake-reflection-prompt-${step - 3}`}
                    text={REFLECTIONS[step - 3]}
                    as="h2"
                    className="font-heading text-3xl sm:text-4xl text-[#F4EFE6] font-light mb-10 leading-snug max-w-2xl"
                    manual
                  />
                  <textarea
                    data-testid={INTAKE.reflectionInput(step - 3)}
                    className="input-sacred resize-none min-h-[140px] leading-relaxed"
                    value={reflections[step - 3]}
                    onChange={(e) => setReflection(step - 3, e.target.value)}
                    placeholder="one or two sentences"
                  />
                  <Reason>No wrong answers. Nothing here is graded.</Reason>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-16 flex items-center justify-between">
          <button
            data-testid={INTAKE.backBtn}
            type="button"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            className="font-overline text-[#F4EFE6]/60 hover:text-[#C8A96E] transition-colors"
            disabled={step === 0}
            style={{ minHeight: 44, minWidth: 60 }}
          >
            ← Back
          </button>

          {step < totalSteps - 1 ? (
            <button
              data-testid={INTAKE.nextBtn}
              type="button"
              onClick={() => setStep((s) => s + 1)}
              disabled={!canAdvance()}
              className="btn-sacred"
              style={{ minHeight: 44 }}
            >
              Continue
            </button>
          ) : (
            <button
              data-testid={INTAKE.submitBtn}
              type="button"
              onClick={submit}
              disabled={!canAdvance() || busy}
              className="btn-sacred btn-sacred-solid"
              style={{ minHeight: 44 }}
            >
              {busy ? "Sealing…" : "Seal my soul record"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
